import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { updatePartnership, uploadPartnershipLogo } from "@/lib/firebase";
import { Loader2, Handshake, Upload, X } from "lucide-react";
import { logger } from "@/lib/logger";

interface Partnership {
  id: string;
  name: string;
  type: string;
  logoUrl: string;
  enabled: boolean;
}

interface EditPartnershipModalProps {
  isOpen: boolean;
  onClose: () => void;
  partnership: Partnership;
}

export default function EditPartnershipModal({
  isOpen,
  onClose,
  partnership,
}: EditPartnershipModalProps) {
  const [name, setName] = useState(partnership.name);
  const [type, setType] = useState(partnership.type);
  const [logoUrl, setLogoUrl] = useState(partnership.logoUrl);
  const [enabled, setEnabled] = useState(partnership.enabled);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>(partnership.logoUrl || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    type?: string;
  }>({});
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      setName(partnership.name);
      setType(partnership.type);
      setLogoUrl(partnership.logoUrl);
      setEnabled(partnership.enabled);
      setLogoPreview(partnership.logoUrl || "");
      setLogoFile(null);
      setErrors({});
    }
  }, [isOpen, partnership]);

  const validateForm = (): boolean => {
    const newErrors: { name?: string; type?: string } = {};

    if (!name.trim()) {
      newErrors.name = "Partnership name is required";
    }

    if (!type) {
      newErrors.type = "Partnership type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file.",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please select an image smaller than 5MB.",
        variant: "destructive",
      });
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview("");
    setLogoUrl("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      logger.log("🤝 Updating partnership:", {
        id: partnership.id,
        name: name.trim(),
        type,
      });

      let finalLogoUrl = logoUrl;

      if (logoFile) {
        finalLogoUrl = await uploadPartnershipLogo(logoFile);
        logger.log("✅ Logo uploaded:", finalLogoUrl);
      }

      await updatePartnership(partnership.id, {
        name: name.trim(),
        type,
        logoUrl: finalLogoUrl,
        enabled,
      });

      logger.log("✅ Partnership updated:", partnership.id);

      toast({
        title: "Partnership updated",
        description: `${name.trim()} has been updated successfully.`,
      });

      onClose();
    } catch (error: any) {
      logger.error("❌ Error updating partnership:", error);
      toast({
        title: "Failed to update partnership",
        description:
          error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Handshake className="h-5 w-5" />
            Edit Partnership
          </DialogTitle>
          <DialogDescription>
            Update the partnership details.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              Partnership Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              placeholder="Enter partnership name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSubmitting}
              data-testid="input-edit-partnership-name"
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">
              Type <span className="text-red-500">*</span>
            </Label>
            <Select value={type} onValueChange={setType} disabled={isSubmitting}>
              <SelectTrigger data-testid="select-edit-partnership-type">
                <SelectValue placeholder="Select partnership type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Partner">Partner</SelectItem>
                <SelectItem value="Reseller">Reseller</SelectItem>
              </SelectContent>
            </Select>
            {errors.type && (
              <p className="text-sm text-red-500">{errors.type}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Logo</Label>
            <div className="flex items-start space-x-4">
              {logoPreview ? (
                <div className="relative">
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="w-20 h-20 object-contain rounded border"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full bg-red-100 hover:bg-red-200"
                    onClick={handleRemoveLogo}
                    disabled={isSubmitting}
                  >
                    <X className="w-3 h-3 text-red-600" />
                  </Button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-20 h-20 border-2 border-dashed rounded-lg cursor-pointer hover:bg-slate-50">
                  <Upload className="w-6 h-6 text-slate-400" />
                  <span className="text-xs text-slate-500 mt-1">Upload</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="hidden"
                    disabled={isSubmitting}
                  />
                </label>
              )}
              <div className="text-sm text-slate-500">
                <p>Upload a logo for the partnership.</p>
                <p>Max size: 5MB</p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="enabled">Enabled</Label>
            <Switch
              id="enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
              disabled={isSubmitting}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
